import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClientProvider, QueryClient } from "react-query";
import { Router } from "react-router-dom";
import { StylesProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { SnackbarProvider } from "notistack";
import { ReactQueryDevtools } from "react-query/devtools";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { createBrowserHistory } from "history";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import DateFnsUtils from "@date-io/date-fns";

import { IconsProvider, ThemeProvider } from "./modules/core/providers";

import RootRoutes from "./modules/core/routes";
import ErrorBoundary from "./modules/core/components/ErrorBoundary";

import { theme } from "modules/core/utils/theme";
import { coreModals } from "modules/core/modals";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dates/styles.css";

const history = createBrowserHistory();

const sentryDSN = import.meta.env.REACT_APP_SENTRY;

if (sentryDSN) {
  Sentry.init({
    dsn: sentryDSN,
    environment: import.meta.env.REACT_APP_SENTRY_ENV
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 2 * 60 * 1000 // 2 minutes
    },
    mutations: {
      retry: false
    }
  }
});

const Root = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <StylesProvider injectFirst>
        <ThemeProvider>
          <MantineProvider theme={theme} classNamesPrefix="ee">
            <ModalsProvider modals={coreModals}>
              <IconsProvider>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <SnackbarProvider>
                    <Router history={history}>
                      <CssBaseline />

                      <RootRoutes />

                      <Notifications />

                      <ReactQueryDevtools
                        initialIsOpen={false}
                        position="bottom-left"
                      />
                    </Router>
                  </SnackbarProvider>
                </MuiPickersUtilsProvider>
              </IconsProvider>
            </ModalsProvider>
          </MantineProvider>
        </ThemeProvider>
      </StylesProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

ReactDOM.createRoot(document.getElementById("root")!).render(<Root />);
